import React, { useState } from 'react';
import { FiSearch, FiFilter, FiPlus, FiDownload, FiChevronDown, FiMessageSquare } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import VerbatimCard from '../components/VerbatimCard';
import { Heading } from '../components/ui/heading';

const VerbatimsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  
  // Mock data for verbatims
  const mockVerbatims = [
    {
      id: 'V-001',
      text: "I've been trying to access my account through the mobile app for three days now. Every time I try to log in, the app crashes. This is unacceptable for a financial service provider.",
      sentiment: 'Negative',
      sentimentScore: 2.3,
      emotions: ['Frustration', 'Anger', 'Disappointment'],
      keywords: ['app', 'crash', 'login', 'unacceptable']
    },
    {
      id: 'V-002',
      text: "I waited 45 minutes to speak with a customer service representative, only to be told they couldn't help me with my issue. Then I was transferred to another department and had to explain everything again. The entire experience was frustrating and time-consuming.",
      sentiment: 'Very Negative',
      sentimentScore: 1.5,
      emotions: ['Frustration', 'Anger', 'Helplessness'],
      keywords: ['wait', 'customer service', 'transfer', 'frustrating']
    },
    {
      id: 'V-003',
      text: "I just noticed that I was charged twice for my monthly subscription. I tried calling customer support, but I've been waiting on hold for over an hour. This is outrageous!",
      sentiment: 'Negative',
      sentimentScore: 2.1,
      emotions: ['Anger', 'Frustration', 'Disbelief'],
      keywords: ['charged twice', 'subscription', 'waiting', 'outrageous']
    },
    {
      id: 'V-004',
      text: "The description of the premium service package clearly stated that it included unlimited usage of all features, but after signing up, I found out there are several 'premium' features that require additional payment. This feels dishonest and misleading.",
      sentiment: 'Negative',
      sentimentScore: 2.4,
      emotions: ['Betrayal', 'Disappointment', 'Frustration'],
      keywords: ['premium', 'misleading', 'dishonest', 'additional payment']
    },
    {
      id: 'V-005',
      text: "The instructions for selecting a plan were really confusing. I thought I was signing up for the basic package, but I was charged for the premium version. When I called to explain, the representative was helpful and switched me to the correct plan immediately.",
      sentiment: 'Mixed',
      sentimentScore: 4.5,
      emotions: ['Confusion', 'Relief', 'Appreciation'],
      keywords: ['confusing', 'instructions', 'charged', 'helpful', 'switched']
    },
    {
      id: 'V-006',
      text: "I'm writing to express my disappointment with the hidden processing fees that were charged to my account. Nowhere during the signup process was I informed about these additional charges. This lack of transparency is concerning.",
      sentiment: 'Negative',
      sentimentScore: 2.7,
      emotions: ['Disappointment', 'Concern', 'Distrust'],
      keywords: ['hidden fees', 'processing fees', 'transparency', 'charges']
    },
  ];

  const filteredVerbatims = mockVerbatims.filter(verbatim => 
    verbatim.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    verbatim.sentiment.toLowerCase().includes(searchTerm.toLowerCase()) ||
    verbatim.emotions.some(emotion => emotion.toLowerCase().includes(searchTerm.toLowerCase())) ||
    verbatim.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <Heading level={1} weight="bold" className="text-3xl mb-2">Verbatims</Heading>
          <p className="text-secondary/70">Customer feedback and verbatim statements</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 rounded-lg text-sm flex items-center bg-white border border-gray-200 text-secondary hover:bg-gray-50 transition-colors">
            <IconWrapper icon={FiDownload} className="mr-2" size={18} />
            Export
          </button>
          <button className="px-4 py-2 rounded-lg text-sm flex items-center bg-primary text-secondary hover:bg-primary/90 transition-colors">
            <IconWrapper icon={FiPlus} className="mr-2" size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <IconWrapper icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary/50" size={20} />
            <input
              type="text"
              placeholder="Search verbatims by content, sentiment, emotions, or keywords..."
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <button 
              className="px-4 py-2 rounded-lg text-sm flex items-center bg-white border border-gray-200 text-secondary hover:bg-gray-50 transition-colors"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <IconWrapper icon={FiFilter} className="mr-2" size={18} />
              Filters
              <IconWrapper icon={FiChevronDown} className={`ml-2 transition-transform ${filterOpen ? 'rotate-180' : ''}`} size={16} />
            </button>
          </div>
        </div>
        
        {filterOpen && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-secondary/70 mb-2">Sentiment</label>
                <select className="w-full p-2 border border-gray-200 rounded-lg">
                  <option value="">All Sentiments</option>
                  <option value="Positive">Positive</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Mixed">Mixed</option>
                  <option value="Negative">Negative</option>
                  <option value="Very Negative">Very Negative</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-secondary/70 mb-2">Emotion</label>
                <select className="w-full p-2 border border-gray-200 rounded-lg">
                  <option value="">All Emotions</option>
                  <option value="Frustration">Frustration</option>
                  <option value="Anger">Anger</option>
                  <option value="Disappointment">Disappointment</option>
                  <option value="Confusion">Confusion</option>
                  <option value="Concern">Concern</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-secondary/70 mb-2">Sentiment Score</label>
                <select className="w-full p-2 border border-gray-200 rounded-lg">
                  <option value="">All Scores</option>
                  <option value="very-negative">Very Negative (0-2)</option>
                  <option value="negative">Negative (2-4)</option>
                  <option value="neutral">Neutral (4-6)</option>
                  <option value="positive">Positive (6-8)</option>
                  <option value="very-positive">Very Positive (8-10)</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <button className="px-4 py-2 rounded-lg text-sm bg-primary text-secondary hover:bg-primary/90 transition-colors mr-2">
                Apply Filters
              </button>
              <button className="px-4 py-2 rounded-lg text-sm text-secondary/70 hover:text-secondary transition-colors">
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Verbatim Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredVerbatims.length > 0 ? (
          filteredVerbatims.map((verbatim) => (
            <VerbatimCard 
              key={verbatim.id} 
              text={verbatim.text}
              sentiment={verbatim.sentiment}
              sentimentScore={verbatim.sentimentScore}
              emotions={verbatim.emotions}
              keywords={verbatim.keywords}
            />
          ))
        ) : (
          <div className="col-span-2 flex flex-col items-center justify-center py-16 text-secondary/70">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <IconWrapper icon={FiMessageSquare} size={32} className="text-secondary/40" />
            </div>
            <p className="text-center text-lg font-medium mb-2">No verbatims found</p>
            <p className="text-center text-sm">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerbatimsList;